import 'components/ordering/multiple-baskets/basket-selector/basket-selector.js'
import type { CSESlideIn } from '@cloudsuite/mosaic-elements'
import { BREAKPOINTS, ConcreteBreakpoints } from '@cloudsuite/mosaic-shared/scripts/breakpoints.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { CSSResultGroup, html, LitElement, nothing, TemplateResult } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { User } from 'components/ordering/multiple-baskets/basket-api/user.js'
import { ProductAddedEvent, ProductRemovedEvent } from 'components/ordering/multiple-baskets/basket-events.js'
import { BasketListItem } from 'components/ordering/multiple-baskets/basket-store/basket-list-parser.js'
import {
  BASKET_LIST_MUTATOR,
  BASKET_LIST_STORE,
  BasketListMutator,
  BasketListStore,
} from 'components/ordering/multiple-baskets/basket-store/basket-list-store.js'
import { renderDeliveryPolicyBadge } from 'components/ordering/multiple-baskets/delivery-policy.js'
import { style } from './basket-header-button.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { breakpoints } from '@cloudsuite/mosaic-shared/styles/breakpoints.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import 'components/ordering/multiple-baskets/basket-creator/basket-creator-slide-in.js'

initializeIconsByRole({
  'csc-basket-header-button.basket': { name: 'shopping-basket', style: 'regular' },
  'csc-basket-header-button.dropdown': { name: 'plus', style: 'regular' },
})

@customElement('csc-basket-header-button')
export class CSCBasketHeaderButton extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @inject(WINDOW_LOCATION)
  private windowLocation!: WindowLocation

  @inject(BASKET_LIST_MUTATOR)
  private basketListMutator!: BasketListMutator

  @inject(BASKET_LIST_STORE)
  private basketListStore!: BasketListStore

  @inject(BREAKPOINTS)
  private breakpoints!: ConcreteBreakpoints

  @property({ attribute: 'basket-index-url' })
  basketIndexUrl = ''

  @property({ attribute: 'show-cumulated-count', type: Boolean })
  showCumulatedCount = false

  @property({ attribute: 'ordering-endpoint' })
  orderingEndpoint = ''

  @property({ type: Number, attribute: 'shop-id' })
  shopId: number | null = null

  @property({ type: Number, attribute: 'party-id' })
  partyId: number | null = null

  @property({ type: Number, attribute: 'user-id' })
  userId: number | null = null

  @property({ type: String, attribute: 'basket-index-button-variant' })
  basketIndexButtonVariant: string = 'primary'

  @property({ type: String, attribute: 'basket-selector-button-variant' })
  basketSelectorButtonVariant: string = ''

  @property({ type: String, attribute: 'basket-count-badge-variant' })
  basketCountBadgeVariant: string = 'warning'

  @property({ type: String, attribute: 'basket-delivery-policy-badge-variant' })
  basketDeliveryPolicyBadgeVariant: string = ''

  private get user(): User {
    if (this.userId !== null && this.partyId !== null && this.shopId !== null) {
      return { userId: this.userId, shopId: this.shopId, partyId: this.partyId }
    }
    throw new Error('user-id, party-id and shop-id are mandatory.')
  }

  @state()
  private selectedBasket: BasketListItem | null = null

  @state()
  private onSmallDevice: boolean = true

  @query('cse-slide-in')
  cseSlideIn!: CSESlideIn

  private renderBadge(): TemplateResult | null {
    const shouldNotRenderBadge = !this.selectedBasket?.deliveryPolicy?.name

    if (this.onSmallDevice || shouldNotRenderBadge) {
      return html`${nothing}`
    }
    return renderDeliveryPolicyBadge(this.selectedBasket?.deliveryPolicy?.name ?? null, true, this.basketDeliveryPolicyBadgeVariant)
  }

  private renderCountBadge(): TemplateResult {
    let count: number = this.selectedBasket?.lineCount ?? 0
    if (this.showCumulatedCount) {
      count = this.selectedBasket?.cumulatedCount ?? 0
    }
    return html`
      <cse-badge class="count" variant="${this.basketCountBadgeVariant}" size="small" composition="floating">
        ${count}
      </cse-badge>
    `
  }

  private basketIndexClicked() {
    this.windowLocation.redirect(new URL(this.basketIndexUrl, window.location.href))
  }

  override async connectedCallback() {
    super.connectedCallback()

    createEffect(() => { this.selectedBasket = this.basketListStore.selectedBasket() })
    createEffect(() => { this.onSmallDevice = this.breakpoints.small() })

    this.breakpoints.matchMedia(breakpoints.medium)
    this.basketListMutator.initialize(this.user)

    window.addEventListener(ProductAddedEvent.type, this.handleBasketCountChanged)
    window.addEventListener(ProductRemovedEvent.type, this.handleBasketCountChanged)
  }

  override disconnectedCallback() {
    window.removeEventListener(ProductAddedEvent.type, this.handleBasketCountChanged)
    window.removeEventListener(ProductRemovedEvent.type, this.handleBasketCountChanged)

    super.disconnectedCallback()
  }

  private handleBasketCountChanged = async () => {
    this.basketListMutator.refreshBasketList()
  }

  protected override render(): TemplateResult {
    return html`
      <div>
        <div class="header-button">
          <cse-button
            variant="${this.basketIndexButtonVariant}"
            size="${this.onSmallDevice ? 'small' : 'default'}"
            fit="fill"
            @click=${this.basketIndexClicked}
            id="basket-index"
          >
            <cse-icon
              icon-role="csc-basket-header-button.basket"
              size="lg"
              slot="prefix"
            ></cse-icon>
            ${!this.onSmallDevice
              ? this.selectedBasket?.name ?? t('basket_name_fallback', { ns: 'csc-basket-header-button', default: 'Basket' })
              : nothing}
          </cse-button>
          <cse-button
            size="${this.onSmallDevice ? 'small' : 'default'}"
            id="basket-selector"
            fit="square"
            @click="${() => this.cseSlideIn.open()}"
            variant="${this.basketSelectorButtonVariant}"
          >
            <cse-icon icon-role="csc-basket-header-button.dropdown"></cse-icon>
          </cse-button>

          <cse-group inline>
            ${this.renderCountBadge()}
            ${this.renderBadge()}
          </cse-group>
        </div>

        <cse-slide-in>
          <div slot="header">
            ${t('slide_in_header', { ns: 'csc-basket-header-button', default: 'Baskets' })}
          </div>
          <csc-basket-selector
            ordering-endpoint='${this.orderingEndpoint}'
            user-id='${this.userId}'
            party-id='${this.partyId}'
            shop-id='${this.shopId}'
            ?show-cumulated-count=${this.showCumulatedCount}
            basket-index-url="${this.basketIndexUrl}"
          ></csc-basket-selector>
          <csc-basket-creator-slide-in slot="footer"
            ordering-endpoint='${this.orderingEndpoint}'
            party-id='${this.partyId}'
            shop-id='${this.shopId}'
          ></csc-basket-creator-slide-in>
        </cse-slide-in>
      </div>
    `
  }
}
